import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, FormGroup, Label } from "reactstrap";
import QwikForm from "components/form/QwikForm";
import QwikInput from "components/form/QwikInput";
import QwikSelect from "components/form/QwikSelect";
import { useUpdateJobApplicationMutation, useGetAllJobsForFilterQuery } from "store/features/jobs/jobApplicationApi";
import { toast } from "sonner";

const EditJobApplicationModal = ({ isOpen, toggle, application, onApplicationUpdate }) => {
  const [updateJobApplication, { isLoading }] = useUpdateJobApplicationMutation();
  const { data: jobs } = useGetAllJobsForFilterQuery();
  const [formData, setFormData] = useState({});

  // Initialize form data when application data changes
  useEffect(() => {
    if (application) {
      setFormData({
        applicantname: application.applicantname || '',
        applicantemail: application.applicantemail || '',
        contactNumber: application.contactNumber || '',
        applicantexperience: application.applicantexperience || '',
        jobid: application.jobid || '',
        title: application.title || '',
        currentSalary: application.currentSalary || '',
        expectedSalary: application.expectedSalary || '',
        noticePeriod: application.noticePeriod || '',
        immediatelyJoining: application.immediatelyJoining || false,
        country: application.country || '',
        englishCommunication: application.englishCommunication || '',
        technologyScore: application.technologyScore || '',
        canadatimezone: application.canadatimezone || '',
        interviewlink: application.interviewlink || '',
        applystatus: application.applystatus || '',
        selectionstatus: application.selectionstatus || '',
        selectforinterview: application.selectforinterview || false,
        interviewjoiningconfarmation: application.interviewjoiningconfarmation || false,
        interviewstatus: application.interviewstatus || '',
        interviewDate: application.interviewDate || '',
        date: application.date || '',
      });
    }
  }, [application]);

  const handleSubmit = async (data) => {
    try {
      const submitData = {
        applicantname: data.applicantname || '',
        applicantemail: data.applicantemail || '',
        contactNumber: data.contactNumber || '',
        applicantexperience: data.applicantexperience || '',
        jobid: data.jobid || '',
        title: data.title || '',
        currentSalary: data.currentSalary || '',
        expectedSalary: data.expectedSalary || '',
        noticePeriod: data.noticePeriod || '',
        immediatelyJoining: data.immediatelyJoining || false,
        country: data.country || '',
        englishCommunication: data.englishCommunication || '',
        technologyScore: data.technologyScore || '',
        canadatimezone: data.canadatimezone || '',
        interviewlink: data.interviewlink || '',
        applystatus: data.applystatus || '',
        selectionstatus: data.selectionstatus || '',
        selectforinterview: data.selectforinterview || false,
        interviewjoiningconfarmation: data.interviewjoiningconfarmation || false,
        interviewstatus: data.interviewstatus || '',
        interviewDate: data.interviewDate || '',
        date: data.date || '',
      };

      console.log("handleSubmit", submitData);
      await updateJobApplication({
        id: application.id,
        formData: submitData
      }).unwrap();

      toast.success("Job application updated successfully");
      // onApplicationUpdate();
      handleModalClose();
    } catch (error) {
      toast.error("Failed to update job application");
      console.error('Job application update error:', error);
    }
  };

  const handleModalClose = () => {
    toggle();
  };

  // Get job options for dropdown
  const jobOptions = jobs ? [
    { value: "", label: "General Application" },
    ...jobs.map(job => ({
      value: job.id.toString(),
      label: `${job.title} (${job.company})`
    }))
  ] : [{ value: "", label: "Loading jobs..." }];

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose} size="lg">
      <ModalHeader toggle={handleModalClose}>
        Edit Application - {application?.applicantname}
      </ModalHeader>
      <QwikForm onSubmit={handleSubmit} defaultValues={application}>
        <ModalBody>
          <Row>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="applicantname"
                  label="Applicant Name"
                  placeholder="Enter applicant name"
                  type="text"
                  className="form-control"
                  required
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="applicantemail"
                  label="Email"
                  placeholder="Enter email address"
                  type="email"
                  className="form-control"
                  required
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="contactNumber"
                  label="Contact Number"
                  placeholder="Enter contact number"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="applicantexperience"
                  label="Experience (Years)"
                  placeholder="Enter years of experience"
                  type="number"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="jobid"
                  label="Applied Job"
                  options={jobOptions}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="title"
                  label="Job Title"
                  placeholder="Enter job title"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="currentSalary"
                  label="Current Salary"
                  placeholder="Enter current salary"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="expectedSalary"
                  label="Expected Salary"
                  placeholder="Enter expected salary"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="noticePeriod"
                  label="Notice Period"
                  placeholder="e.g., 30 days, Immediately"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="immediatelyJoining"
                  label="Immediate Joining"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" }
                  ]}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="country"
                  label="Country"
                  placeholder="Enter country"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="englishCommunication"
                  label="English Communication"
                  placeholder="e.g., Good, Average, Excellent"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="technologyScore"
                  label="Technology Score"
                  placeholder="e.g., 8/10"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="applystatus"
                  label="Application Status"
                  options={[
                    { value: "", label: "Select Status" },
                    { value: "applied", label: "Applied" },
                    { value: "under_review", label: "Under Review" },
                    { value: "shortlisted", label: "Shortlisted" },
                  ]}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="selectionstatus"
                  label="Selection Status"
                  options={[
                    { value: "", label: "Select Status" },
                    { value: "pending", label: "Pending" },
                    { value: "selected", label: "Selected" },
                    { value: "rejected", label: "Rejected" },
                  ]}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="selectforinterview"
                  label="Select for Interview"
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" }
                  ]}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikSelect
                  name="interviewstatus"
                  label="Interview Status"
                  options={[
                    { value: "", label: "Select Status" },
                    { value: "scheduled", label: "Scheduled" },
                    { value: "completed", label: "Completed" },
                    { value: "cancelled", label: "Cancelled" },
                    { value: "rescheduled", label: "Rescheduled" },
                  ]}
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="interviewDate"
                  label="Interview Date"
                  type="datetime-local"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="interviewlink"
                  label="Interview Link"
                  placeholder="Enter interview meeting link"
                  type="text"
                  className="form-control"
                />
              </div>
            </Col>
            <Col lg={6}>
              <div className="mb-3">
                <QwikInput
                  name="date"
                  label="Application Date"
                  type="date"
                  className="form-control"
                />
              </div>
            </Col>
            {/* <Col lg={12}>
              <div className="mb-3">
                <Label className="form-label">CV Link</Label>
                {application?.cv ? (
                  <div>
                    <a
                      href={application.cv}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="fas fa-external-link-alt me-1"></i> View CV
                    </a>
                    <small className="text-muted d-block mt-1">
                      {application.cv.split('/').pop()}
                    </small>
                  </div>
                ) : (
                  <span className="text-muted">No CV uploaded</span>
                )}
              </div>
            </Col> */}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isLoading}>
            {isLoading ? "Updating..." : "Update Application"}
          </Button>
        </ModalFooter>
      </QwikForm>
    </Modal>
  );
};

export default EditJobApplicationModal;